import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, RefreshCw, Mail, Sparkles, Download } from "lucide-react";
import audeLogoFull from "@/assets/aude-logo-full.png";
import { usePWAInstall } from "@/hooks/usePWAInstall";

const INSTAGRAM_URL = "https://www.instagram.com/auderights/";
const LINKEDIN_URL = "https://www.linkedin.com/in/aude-lavielle-72246451/";

type SubscriptionStatus = "idle" | "loading" | "new" | "resubscribed" | "already";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<SubscriptionStatus>("idle");
  const { isInstallable, install } = usePWAInstall();

  const { data: publishers } = useQuery({
    queryKey: ["footer-publishers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("publishers")
        .select("name, slug")
        .or("archived.is.null,archived.eq.false")
        .order("name")
        .limit(6);
      if (error) throw error;
      return data;
    },
  });

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) return;

    setStatus("loading");
    try {
      const { error: insertError } = await (supabase as any)
        .from("newsletter_subscribers")
        .insert({ 
          email: trimmedEmail,
          source: "website_footer"
        });

      if (insertError) {
        if (insertError.code === "23505") {
          const { data: updateData, error: updateError } = await (supabase as any)
            .from("newsletter_subscribers")
            .update({ 
              status: "active",
              unsubscribed_at: null
            })
            .eq("email", trimmedEmail)
            .eq("status", "unsubscribed")
            .select("id");

          if (updateError) throw updateError;

          if (updateData && updateData.length > 0) {
            setStatus("resubscribed");
            setEmail("");
            setTimeout(() => setStatus("idle"), 6000);
          } else {
            setStatus("already");
            setTimeout(() => setStatus("idle"), 4000);
          }
        } else {
          throw insertError;
        }
      } else {
        setStatus("new");
        setEmail("");
        setTimeout(() => setStatus("idle"), 6000);
      }
    } catch (error) {
      console.error("Subscription error:", error);
      setStatus("idle");
    }
  };

  const resetForm = () => setStatus("idle");

  const handleSocialClick = (url: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <footer className="bg-secondary/50 mt-auto border-t border-border">
      <div className="container mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Logo */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1 flex flex-col items-start">
            <Link to="/" className="shrink-0">
              <img 
                src={audeLogoFull} 
                alt="Aude Rights Agency" 
                width={80}
                height={80}
                className="h-16 lg:h-20 w-auto"
              />
            </Link>
            {/* Social icons with micro-interactions */}
            <div className="flex items-center gap-3 mt-5">
              <button
                type="button"
                onClick={handleSocialClick(INSTAGRAM_URL)}
                className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-300 cursor-pointer btn-press hover:scale-110"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </button>
              <button
                type="button"
                onClick={handleSocialClick(LINKEDIN_URL)}
                className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-300 cursor-pointer btn-press hover:scale-110"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </button>
            </div>
          </div>

          {/* MAIN */}
          <div>
            <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-4">
              Main
            </h3>
            <nav className="flex flex-col gap-2.5">
              <Link to="/" className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 transition-all duration-200">Home</Link>
              <Link to="/about" className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 transition-all duration-200">About Us</Link>
              <Link to="/books" className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 transition-all duration-200">Books</Link>
              <Link to="/contact" className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 transition-all duration-200">Contact Us</Link>
            </nav>
          </div>

          {/* LEGAL */}
          <div>
            <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-4">
              Legal
            </h3>
            <nav className="flex flex-col gap-2.5">
              <Link to="/legal" className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 transition-all duration-200">Legal Notice</Link>
              <Link to="/privacy" className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 transition-all duration-200">Privacy Policy</Link>
              <Link to="/cookies" className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 transition-all duration-200">Cookie Policy</Link>
            </nav>
          </div>

          {/* PUBLISHERS */}
          <div>
            <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-4">
              Publishers
            </h3>
            <nav className="flex flex-col gap-2.5">
              {publishers?.map((publisher) => (
                <Link 
                  key={publisher.slug}
                  to={`/publishers/${publisher.slug}`} 
                  className="text-sm text-foreground/70 hover:text-primary hover:translate-x-1 transition-all duration-200"
                >
                  {publisher.name}
                </Link>
              ))}
              <Link 
                to="/publishers" 
                className="text-sm text-primary font-medium hover:underline mt-1"
              >
                See all →
              </Link>
            </nav>
          </div>

          {/* NEWSLETTER - Enhanced visual feedback */}
          <div className="col-span-2 md:col-span-1 lg:col-span-2">
            <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-4">
              Newsletter
            </h3>
            <p className="text-sm text-foreground/70 mb-4">
              Stay updated on new books and publishing opportunities.
            </p>
            
            {/* New subscription success */}
            {status === "new" && (
              <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 border border-primary/30 animate-fade-in">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(var(--primary)/0.15),transparent_70%)]" />
                <div className="relative flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-primary animate-pulse" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Welcome aboard!</p>
                    <p className="text-sm text-muted-foreground">You're now subscribed to our newsletter.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Resubscribed success */}
            {status === "resubscribed" && (
              <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-r from-emerald-500/20 via-emerald-500/10 to-emerald-500/20 border border-emerald-500/30 animate-fade-in">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(142_76%_36%/0.15),transparent_70%)]" />
                <div className="relative flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <RefreshCw className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Welcome back!</p>
                    <p className="text-sm text-muted-foreground">Great to have you with us again.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Already subscribed */}
            {status === "already" && (
              <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-amber-500/20 border border-amber-500/30 animate-fade-in">
                <div className="relative flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">Already subscribed</p>
                    <p className="text-sm text-muted-foreground">This email is already on our list.</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetForm}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Try another
                  </Button>
                </div>
              </div>
            )}

            {/* Form */}
            {(status === "idle" || status === "loading") && (
              <form onSubmit={handleSubscribe} className="flex gap-2 animate-fade-in">
                <Input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-background border-border/50 h-10 focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                  required
                  disabled={status === "loading"}
                />
                <Button 
                  type="submit" 
                  size="sm"
                  disabled={status === "loading"}
                  className="h-10 px-4 btn-press"
                  aria-label="Subscribe to newsletter"
                >
                  {status === "loading" ? (
                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Copyright + Install Button */}
      <div className="border-t border-border">
        <div className="container mx-auto px-6 lg:px-12 py-5 flex items-center justify-center gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Aude Rights Agency. All rights reserved.
          </p>
          {isInstallable && (
            <button
              onClick={install}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:scale-105"
            >
              <Download className="h-3.5 w-3.5" />
              <span>Install App</span>
            </button>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
